/**
 * Keyboard shortcut registry (desktop-first, §visual style).
 * Combo syntax: modifiers joined with '+', then the key, e.g. 'ctrl+k',
 * 'ctrl+shift+s'. 'ctrl' matches Ctrl on Windows/Linux and ⌘ on macOS.
 *
 * One system, one registry: views register their own combos here while
 * mounted (the GM screen's '/' focuses its search box) and unregister on
 * unmount. A MODIFIER-LESS combo never fires while the user is typing in a
 * text field — the character belongs to the field ('/' inside the GM screen's
 * search box types a slash instead of refocusing it). A MODIFIED combo may
 * opt into the SAME guard with `guardTyping: true` — undo/redo does, because
 * inside a text field the browser's NATIVE undo is what the user wants.
 */

export interface Shortcut {
  combo: string;
  /** i18n key describing the action (for the shortcuts help panel). */
  descriptionKey: string;
  handler: (e: KeyboardEvent) => void;
  /**
   * Suppress the combo while the user is typing in a text field, WITHOUT
   * preventDefault — the keydown stays with the field (its native undo
   * keeps working). Defaults to false: most modified combos (ctrl+k …)
   * intentionally fire everywhere.
   */
  guardTyping?: boolean;
}

const registry = new Map<string, Shortcut>();
let installed: (() => void) | null = null;

function comboOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  const primary = e.ctrlKey || e.metaKey;
  if (primary) parts.push('ctrl');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  parts.push(e.key.toLowerCase());
  return parts.join('+');
}

/** Text-ish controls a user types INTO — a plain key must reach them. */
const TYPING_INPUT_TYPES = new Set([
  '',
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
]);

function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null || !(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return TYPING_INPUT_TYPES.has((target as HTMLInputElement).type);
  return false;
}

export function registerShortcut(shortcut: Shortcut): () => void {
  registry.set(shortcut.combo, shortcut);
  return () => {
    // Only remove if it is still *our* registration (re-registration safe).
    if (registry.get(shortcut.combo) === shortcut) registry.delete(shortcut.combo);
  };
}

export function listShortcuts(): Shortcut[] {
  return [...registry.values()];
}

/** Global keydown installer — call once from the app shell. */
export function installKeyboard(): () => void {
  if (installed) return installed;
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.defaultPrevented) return;
    const combo = comboOf(e);
    const shortcut = registry.get(combo);
    if (!shortcut) return;
    // A MODIFIER-LESS combo ('/') is a character: while focus sits in a text
    // field the field wins, so the GM screen's "/" shortcut never steals a "/"
    // typed into the search box. Modified combos (ctrl+k …) still fire anywhere
    // UNLESS they opted into the same guard (`guardTyping`) — undo/redo does,
    // so Ctrl+Z inside an input reaches the field's native undo untouched.
    // The guard runs BEFORE preventDefault: a suppressed key is not ours.
    const typingGuarded = !combo.includes('+') || shortcut.guardTyping === true;
    if (typingGuarded && isTypingTarget(e.target)) return;
    e.preventDefault();
    shortcut.handler(e);
  };
  window.addEventListener('keydown', onKeyDown);
  const dispose = (): void => {
    window.removeEventListener('keydown', onKeyDown);
    installed = null;
  };
  installed = dispose;
  return dispose;
}
